import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CircleNotch } from "@phosphor-icons/react";

const LS_KEY = "lottie_import_job";

export default function ImportIndicator() {
  const [job, setJob] = useState(null); // { id, imported }
  const pollRef = useRef(null);

  useEffect(() => {
    const stop = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

    const start = (jobId) => {
      if (!jobId || pollRef.current) return;
      localStorage.setItem(LS_KEY, jobId);
      setJob({ id: jobId, imported: 0 });
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/import/jobs/${jobId}`);
          setJob({ id: jobId, imported: data.imported || 0 });
          if (data.status === "done") {
            stop(); localStorage.removeItem(LS_KEY); setJob(null);
            toast.success(`Import complete — ${data.imported || 0} new item(s) imported.`);
            const skipped = data.skipped_sources || [];
            if (skipped.length) {
              const names = [...new Set(skipped.map((s) => s.source))].join(", ");
              toast.warning(`Some Xero data was skipped (${names}). Reconnect Xero to grant full access.`, { duration: 8000 });
            }
            window.dispatchEvent(new Event("import:done"));
            window.dispatchEvent(new Event("badges:refresh"));
          } else if (data.status === "error") {
            stop(); localStorage.removeItem(LS_KEY); setJob(null);
            toast.error(data.error || "Import failed");
          }
        } catch (e) {
          if (e.response?.status === 404) { stop(); localStorage.removeItem(LS_KEY); setJob(null); }
        }
      }, 2500);
    };

    const existing = localStorage.getItem(LS_KEY);
    if (existing) start(existing);
    const onStart = (e) => start(e.detail);
    window.addEventListener("import:started", onStart);
    return () => { window.removeEventListener("import:started", onStart); stop(); };
  }, []);

  if (!job) return null;
  return (
    <div data-testid="import-indicator"
      className="fixed bottom-24 right-6 z-40 flex items-center gap-2.5 pl-3.5 pr-4 py-2.5 rounded-full bg-navy text-white text-xs font-semibold shadow-xl border border-white/10">
      <CircleNotch size={15} className="animate-spin text-orangeink" weight="bold" />
      Importing from Xero… <span className="tabular text-white/70">{job.imported} so far</span>
    </div>
  );
}
